import {
  LessonDetailsPublic,
  SandboxLessonDetails,
  SandboxTab,
  VideoLessonDetails,
} from "sj-ui-toolkit/src/client";

import type { HandsOn, Quiz, Video } from "@securityjourney/factory/lesson.schema";

const SandboxTypes = [
  "HandsOnLesson",
  "CodingExercise",
  "CodingChallenge",
  "CtfChallenge",
  "AppLab",
];
const VideoTypes = ["VideoLesson", "VideoWithCodingExercise"];

export function convertLessonDetails(
  publicDetails: LessonDetailsPublic
): SandboxLessonDetails | VideoLessonDetails {
  if (SandboxTypes.includes(publicDetails.lessonType!)) {
    return convertHandsOn(publicDetails);
  } else if (VideoTypes.includes(publicDetails.lessonType!)) {
    return convertVideo(publicDetails);
  } else {
    throw new Error(`Unsupported lesson type: ${publicDetails.lessonType}`);
  }
}

function convertVideo(publicDetails: LessonDetailsPublic): VideoLessonDetails {
  const config = publicDetails.lessonConfiguration as { video?: Video; quiz?: Quiz };

  return {
    lessonKey: publicDetails.lessonKey!,
    lessonVersion: 1,
    title: publicDetails.lessonName ?? "",
    description: publicDetails.lessonDescription,
    completionType: "quiz",
    videoId: config.video!.videoId!,
  };
}

function convertHandsOn(publicDetails: LessonDetailsPublic): SandboxLessonDetails {
  const config = publicDetails.lessonConfiguration as HandsOn;

  const isCtf = publicDetails.lessonType === "CtfChallenge";
  const completionType = isCtf ? "flagSubmission" : config.tests ? "tests" : "steps";
  const hasCodeTests = config.tests?.kind === "CodeSubmission";
  const hasFinishButton = !config.tests && !isCtf;
  const replEngineParam = config.parameters?.find(
    p => p.programmingLanguages?.key === "repl-engine"
  );
  const engines = replEngineParam?.programmingLanguages?.allowedValues ?? [];

  const steps = convertSteps(config);
  const tasks = convertTasks(config);
  const tests = convertTests(config);
  const tabs = convertTabs(config, tests, !!replEngineParam);

  const details: SandboxLessonDetails = {
    lessonKey: publicDetails.lessonKey!,
    lessonVersion: 1,
    lessonType: publicDetails.lessonType,
    title: publicDetails.lessonName ?? "",
    description: publicDetails.lessonDescription,
    completionType,
    hasCodeTests,
    isCtf,
    hasFinishButton,
    engines,
    tests,
    sandbox: {
      instructions: {
        steps,
        tasks,
        requiresFlagSubmission: isCtf,
      },
      tabs,
    },
  };

  return details;
}

function convertSteps(config: HandsOn) {
  const steps =
    config.instructions?.steps.map(s => ({
      content: {
        markdown: s.step ?? "",
      },
      hints:
        s.hints?.map(h => ({
          id: h.id,
          title: h.title,
          cost: h.cost,
        })) ?? [],
    })) ?? [];
  return steps;
}

function convertTasks(config: HandsOn) {
  const tasks = config.instructions?.tasks?.map(t => ({
    content: {
      markdown: t,
    },
  }));

  return tasks;
}

function convertTests(config: HandsOn) {
  // JSON schema doesn't handle the test case ID/failure code mutual exclusivity
  // gracefully. In reality, one of them should always be present when the lesson is modified in factory
  const testCases = config.tests?.testCases.map(tc => {
    return {
      ...tc,
      id: tc.id ?? "",
      failureCode: tc.failureCode ?? "",
    };
  });
  const tests = {
    kind: (config.tests?.kind ?? "None") as "CodeSubmission" | "VerifySandbox" | "None",
    testCases: testCases ?? [],
  };
  return tests;
}

function convertTabs(
  config: HandsOn,
  tests: SandboxLessonDetails["tests"],
  hasReplEngineParam: boolean
) {
  const interceptTabs: string[] = [];

  let lastAppTabIndex = 0;
  let interceptOnLoad = false;

  const tabs: SandboxTab[] =
    config.tabs
      ?.map<SandboxTab>((t, idx) => {
        if (t.app) {
          const name = t.containerName ?? "";
          if (t.app.proxied !== undefined) {
            interceptTabs.push(name);
            lastAppTabIndex = idx;
            interceptOnLoad = interceptOnLoad || !!t.app.proxied.defaultOn;
          }
          return {
            type: "app" as const,
            title: t.renderedName ?? `Tab ${idx + 1}`,
            app: {
              name,
              navigation: t.app.navControls,
            },
          };
        } else if (t.editor) {
          return {
            type: "editor" as const,
            title: t.renderedName ?? `Tab ${idx + 1}`,
            editor: {
              defaultFile: t.editor.defaultFile ?? "",
            },
          };
        } else {
          return { type: "unknown" as const, title: t.renderedName ?? `Tab ${idx + 1}` };
        }
      })
      // Assume that only repl-based lessons need the UI-rendered code editor
      .filter(tab => hasReplEngineParam || tab.type !== "editor") ?? [];

  if (interceptTabs.length > 0) {
    tabs.splice(lastAppTabIndex + 1, 0, {
      type: "intercept" as const,
      title: "Proxy History",
      intercept: {
        apps: interceptTabs,
        activeAfterSandboxLoad: interceptOnLoad,
      },
    });
  }

  // We need a tests tab for VerifySandbox and CodeSubmission based tests
  if (tests && tests.kind !== "None") {
    tabs.push({
      type: "tests" as const,
      title: "Tests",
      tests,
    });
  }

  return tabs;
}
